import { motion } from 'framer-motion';
import { Flower2, Heart, Calendar, Gift, Truck, Store, Building2, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const mainServices = [
  {
    icon: Flower2,
    title: 'Bouquets & Compositions',
    description: 'Des créations florales uniques, confectionnées avec des fleurs fraîches de saison. Chaque bouquet est une œuvre d\'art personnalisée selon vos envies et l\'occasion.',
    features: ['Fleurs de saison', 'Compositions sur mesure', 'Emballage soigné', 'Conseil personnalisé'],
    image: 'https://images.unsplash.com/photo-1487530811176-3780de880c2d?w=800&q=80',
  },
  {
    icon: Heart,
    title: 'Mariages',
    description: 'Sublimez votre jour J avec nos créations florales sur mesure. Du bouquet de la mariée à la décoration de la salle, nous vous accompagnons pour faire de votre mariage un moment inoubliable.',
    features: ['Bouquet de mariée', 'Boutonnières', 'Décoration de table', 'Arche florale'],
    image: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=800&q=80',
  },
  {
    icon: Calendar,
    title: 'Événements',
    description: 'Pour vos événements professionnels ou privés, nous créons des ambiances florales uniques qui marqueront les esprits de vos invités.',
    features: ['Réceptions', 'Conférences', 'Inaugurations', 'Fêtes privées'],
    image: 'https://images.unsplash.com/photo-1469371670807-013ccf25f16a?w=800&q=80',
  },
  {
    icon: Gift,
    title: 'Deuil & Hommages',
    description: 'Dans les moments difficiles, nous vous accompagnons avec délicatesse. Nos compositions florales rendent hommage à vos proches avec respect et élégance.',
    features: ['Gerbes', 'Couronnes', 'Coussins', 'Compositions personnalisées'],
    image: 'https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=800&q=80',
  },
];

const additionalServices = [
  {
    icon: Truck,
    title: 'Livraison',
    description: 'Livraison rapide à Sion et dans les environs. Vos fleurs arrivent fraîches et à temps pour chaque occasion.',
  },
  {
    icon: Store,
    title: 'Retrait en magasin',
    description: 'Commandez en avance et récupérez vos créations dans notre boutique au moment qui vous convient.',
  },
  {
    icon: Building2,
    title: 'Abonnement entreprise',
    description: 'Fleurissez vos locaux professionnels avec des compositions régulièrement renouvelées.',
  },
  {
    icon: Sparkles,
    title: 'Cours de composition',
    description: 'Apprenez l\'art floral lors de nos ateliers créatifs organisés tout au long de l\'année.',
  },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page" className="pt-24">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-block text-accent font-accent italic text-lg mb-4"
            >
              Notre expertise à votre service
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-semibold text-primary tracking-tight"
            >
              Nos Services
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-6 text-lg text-muted-foreground"
            >
              De la création artisanale à la livraison soignée, nous accompagnons tous vos moments 
              avec passion et expertise.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-16 md:py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-24">
          {mainServices.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              data-testid={`service-detail-${index}`}
              className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
              }`}
            >
              {/* Image */}
              <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                <div className="relative">
                  <div className="absolute -inset-4 bg-accent/10 rounded-3xl blur-2xl" />
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-[400px] object-cover"
                    />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-primary" />
                </div>
                <h2 className="font-serif text-3xl md:text-4xl font-semibold text-primary mb-4">
                  {service.title}
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                  {service.description}
                </p>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {service.features.map((feature) => (
                    <div key={feature} className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-accent" />
                      <span className="text-sm text-muted-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
                <Link to="/contact" data-testid={`service-cta-${index}`}>
                  <Button className="rounded-full px-8 py-6 text-lg bg-primary hover:bg-primary/90">
                    Demander un devis
                  </Button>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-serif text-3xl md:text-4xl font-semibold text-primary"
            >
              Services complémentaires
            </motion.h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {additionalServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-3xl border border-primary/5 hover:shadow-xl hover:-translate-y-1 transition-all"
              >
                <div className="w-12 h-12 rounded-2xl bg-secondary flex items-center justify-center mb-5">
                  <service.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-primary mb-3">
                  {service.title}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold mb-6"
          >
            Prêt à fleurir vos moments ?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-primary-foreground/80 text-lg mb-10"
          >
            Contactez-nous pour discuter de votre projet ou passez nous voir en boutique.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="flex flex-wrap justify-center gap-4"
          >
            <Link to="/contact" data-testid="services-contact-btn">
              <Button size="lg" className="rounded-full px-10 py-6 text-lg bg-primary-foreground text-primary hover:bg-primary-foreground/90">
                Nous contacter
              </Button>
            </Link>
            <a href="tel:+41273232316" data-testid="services-phone-btn">
              <Button size="lg" variant="outline" className="rounded-full px-10 py-6 text-lg border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                Appeler maintenant
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export { ServicesPage };
